import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SeoHead from "@shared/components/Seo/SeoHead";
import MarketingShell, { FinalCta, ProductFrame } from "./MarketingShell";
import { capabilityCards, useCases, workflow } from "./content";

export default function VideoBriefHome() {
  return (
    <MarketingShell>
      <SeoHead
        title="The GTM Video Platform"
        description="Turn webinars, demos, interviews, presentations, and events into searchable intelligence, clips, and GTM knowledge."
        path="/"
      />

      <section className="vb-hero">
        <div className="vb-container">
          <span className="vb-eyebrow">THE GTM VIDEO PLATFORM</span>
          <h1 className="vb-display">
            Extract Intel.
            <span className="vb-gradient-text">Ship Clips.</span>
          </h1>
          <p className="vb-lead">
            Turn webinars, demos, interviews, presentations, and events into intelligence,
            clips, and searchable knowledge your GTM team can actually use.
          </p>
          <div className="vb-actions">
            <Link to="/register" className="vb-button vb-button--primary">
              Try VideoBrief Free <ArrowRight size={17} />
            </Link>
            <a href="#demo" className="vb-button vb-button--secondary">See It in Action</a>
          </div>
          <p className="vb-hero__note">Start with a real video. No credit card required.</p>
        </div>
      </section>

      <section className="vb-hero-product" id="product">
        <div className="vb-container">
          <ProductFrame
            className="vb-product-frame--hero"
            src="/marketing/videobrief/chapters.jpg"
            alt="VideoBrief organizing a long webinar into searchable chapters and exact quotes"
            label="A searchable brief, generated from one video"
            eager
          />
        </div>
      </section>

      <section className="vb-section vb-section--soft" id="workflow">
        <div className="vb-container">
          <div className="vb-section-heading">
            <span className="vb-eyebrow">FROM RECORDING TO GTM ASSET</span>
            <h2>Your video is full of GTM value. VideoBrief gets it out.</h2>
            <p>Move from hours of footage to the exact insight, proof, or clip your team needs.</p>
          </div>
          <div className="vb-workflow">
            {workflow.map(({ title, description, icon: Icon }) => (
              <article className="vb-workflow__item" key={title}>
                <span className="vb-icon-tile"><Icon size={21} /></span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="vb-section" id="use-cases">
        <div className="vb-container">
          <div className="vb-section-heading">
            <span className="vb-eyebrow">ONE PLATFORM. YOUR ENTIRE GTM MOTION.</span>
            <h2>The same video becomes useful in four different ways.</h2>
            <p>Start with the job your team needs done. VideoBrief turns the source into an asset they can use now—and find again later.</p>
          </div>
          <div className="vb-use-case-grid">
            {useCases.map(({ slug, label, title, description, icon: Icon }) => (
              <Link className="vb-use-case-card" to={`/${slug}`} key={slug}>
                <span className="vb-icon-tile"><Icon size={21} /></span>
                <span className="vb-use-case-card__label">{label}</span>
                <h3>{title}</h3>
                <p>{description}</p>
                <span className="vb-use-case-card__link">Explore {label} <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="vb-section vb-section--soft">
        <div className="vb-container">
          <div className="vb-section-heading vb-section-heading--center">
            <span className="vb-eyebrow">VIDEO INTELLIGENCE THAT TRAVELS</span>
            <h2>Most video tools help you make video. VideoBrief helps you use it.</h2>
          </div>
          <div className="vb-capabilities">
            {capabilityCards.map(({ title, description, icon: Icon }) => (
              <article className="vb-capability" key={title}>
                <span className="vb-icon-tile"><Icon size={21} /></span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="vb-section vb-section--dark" id="demo">
        <div className="vb-container vb-demo">
          <div className="vb-section-heading">
            <span className="vb-eyebrow vb-eyebrow--light">SEE THE WORKFLOW</span>
            <h2>Long-form video in. Searchable GTM value out.</h2>
            <p style={{ color: "#c8cee0" }}>
              See how VideoBrief chapters a recording, extracts its strongest quotes, and
              turns selected moments into clips.
            </p>
          </div>
          <div className="vb-demo__video">
            <iframe
              src="https://player.mux.com/sXox2LgAfDyHh01A8HMCRHqFK66JusLtq?metadata-video-title=VideoBrief+Product+Demo&video-title=VideoBrief+Product+Demo"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
              title="VideoBrief product demo"
            />
          </div>
        </div>
      </section>

      <FinalCta />
    </MarketingShell>
  );
}
